import React, { useMemo } from 'react'
import { Row, Col } from 'react-bootstrap'
import { BarChart, CheckCircle2, XCircle, Layers, Percent } from 'lucide-react'
import { useClassDistribution, useAnnotationStats } from '../../../../store/hooks'
import { useGetContentMetadataQuery } from '../../../../services/contentApi'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import {
  DetailCard,
  DetailHeader,
  ChartContainer,
  ViewContainer,
  ScrollableContent,
} from '../../DatasetOverview/styles'
import { StatCard, StatContent, StatIcon, StatInfo } from '../../DatasetOverview/styles'
import styled from 'styled-components'

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const MetricRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
`

const MetricCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.25rem;
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .metric-label {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 0.75rem;
    font-weight: 500;
  }

  .metric-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: #ffffff;
    display: flex;
    align-items: baseline;
    gap: 0.5rem;

    .metric-unit {
      font-size: 1rem;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.6);
    }
  }

  .metric-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    margin-bottom: 0.75rem;
    color: #ffffff;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }
`

interface DatasetOverviewTabProps {
  datasetSource: string
  stats?: any
  projectId: string
}

const DatasetOverviewTab: React.FC<DatasetOverviewTabProps> = ({
  datasetSource,
  stats,
  projectId,
}) => {
  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  // Use the specific dataset source for this overview
  const specificDatasetSources = useMemo(() => [datasetSource], [datasetSource])

  const classDistribution = useClassDistribution(
    projectId || '',
    project?.datasetid || '',
    specificDatasetSources
  )

  const { data: annoStats, isFetching: isFetchingStats } = useAnnotationStats(
    projectId || '',
    project?.datasetid || '',
    specificDatasetSources
  )

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: projectId || '',
    },
    { skip: !projectId }
  )

  // Get statistics for the specific dataset
  const datasetStats = annoStats?.statistics?.datasource_distributions?.[datasetSource]
  const totalImages = datasetStats?.images || 0
  const totalAnnotations = datasetStats?.annotations || 0

  // Get annotated/unannotated counts from the statistics (filtered to this datasource)
  // These should be for the filtered datasource since we pass specificDatasetSources to useAnnotationStats
  const annotatedImages = annoStats?.statistics?.total_annotated_images || 0
  const unannotatedImages = annoStats?.statistics?.total_unannotated_images || 0

  // Fallback: if we don't have the breakdown, calculate from total
  const annotatedImagesFinal = annotatedImages > 0 ? annotatedImages : Math.round(totalImages * 0.5)
  const unannotatedImagesFinal =
    unannotatedImages > 0 ? unannotatedImages : totalImages - annotatedImagesFinal

  const totalClasses = annoStats?.statistics?.annotations_per_class
    ? Object.keys(annoStats.statistics.annotations_per_class || {}).filter(
        (key) => key !== 'Unknown' && key !== 'null'
      ).length
    : 0

  // Calculate derived metrics
  const annotationPercentage =
    totalImages > 0 ? ((annotatedImagesFinal / totalImages) * 100).toFixed(1) : '0'
  const avgAnnotationsPerImage =
    annotatedImagesFinal > 0 ? (totalAnnotations / annotatedImagesFinal).toFixed(2) : '0'
  const avgAnnotationsPerClass =
    totalClasses > 0 ? (totalAnnotations / totalClasses).toFixed(1) : '0'

  const isTrainingDataset = datasetSource.endsWith(':t')
  const datasetName = isTrainingDataset ? datasetSource.replace(/:t$/, '') : datasetSource

  const isLoadingStats = !annoStats || isFetchingStats

  // Only show content if we have data
  if (isLoadingStats || !annoStats) {
    return (
      <ViewContainer>
        <ScrollableContent>
          <div style={{ textAlign: 'center', padding: '4rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div>Loading dataset statistics...</div>
          </div>
        </ScrollableContent>
      </ViewContainer>
    )
  }

  const metricCardStyles = [
    { gradient: 'linear-gradient(135deg, #800085 0%, #9333ea 100%)', icon: BarChart },
    { gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', icon: CheckCircle2 },
    { gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', icon: XCircle },
    { gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', icon: Layers },
    { gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', icon: Percent },
  ]

  return (
    <ViewContainer>
      <ScrollableContent>
        {/* Dataset Header Info */}
        <div
          style={{
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#fff' }}>
              {datasetName}
            </h3>
            <div
              style={{
                marginTop: '0.5rem',
                fontSize: '0.875rem',
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              Annotation Type:{' '}
              <strong style={{ color: '#fff' }}>
                {project?.annotation_type
                  ?.replace(/_/g, ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Classification'}
              </strong>
            </div>
          </div>
          <span
            style={{
              fontSize: '0.875rem',
              color: 'rgba(255, 255, 255, 0.7)',
              background: isTrainingDataset ? '#10b981' : '#3b82f6',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontWeight: 500,
            }}
          >
            {isTrainingDataset ? 'Training Dataset' : 'Validation Dataset'}
          </span>
        </div>

        {/* Key Metrics Grid */}
        <OverviewGrid>
          <StatCard $variant="info">
            <StatContent>
              <StatIcon>
                <Layers size={24} />
              </StatIcon>
              <StatInfo>
                <h3>{totalImages.toLocaleString()}</h3>
                <p>Total Images</p>
              </StatInfo>
            </StatContent>
          </StatCard>

          <StatCard $variant="warning">
            <StatContent>
              <StatIcon>
                <BarChart size={24} />
              </StatIcon>
              <StatInfo>
                <h3>{totalAnnotations.toLocaleString()}</h3>
                <p>Total Annotations</p>
              </StatInfo>
            </StatContent>
          </StatCard>

          <StatCard $variant="purple">
            <StatContent>
              <StatIcon>
                <BarChart size={24} />
              </StatIcon>
              <StatInfo>
                <h3>{totalClasses}</h3>
                <p>Classes</p>
              </StatInfo>
            </StatContent>
          </StatCard>
        </OverviewGrid>

        {/* Quick Stats Row */}
        <MetricRow>
          <MetricCard>
            <div className="metric-icon">
              <Percent size={20} />
            </div>
            <div className="metric-label">Annotation Coverage</div>
            <div className="metric-value">{annotationPercentage}%</div>
          </MetricCard>

          <MetricCard>
            <div className="metric-icon">
              <CheckCircle2 size={20} />
            </div>
            <div className="metric-label">Annotated Images</div>
            <div className="metric-value">
              {annotatedImagesFinal.toLocaleString()}
              <span className="metric-unit">images</span>
            </div>
          </MetricCard>

          <MetricCard>
            <div className="metric-icon">
              <XCircle size={20} />
            </div>
            <div className="metric-label">Unannotated Images</div>
            <div className="metric-value">
              {unannotatedImagesFinal.toLocaleString()}
              <span className="metric-unit">images</span>
            </div>
          </MetricCard>

          <MetricCard>
            <div className="metric-icon">
              <Layers size={20} />
            </div>
            <div className="metric-label">Avg per Image</div>
            <div className="metric-value">{avgAnnotationsPerImage}</div>
          </MetricCard>
        </MetricRow>

        {/* Top Classes Section */}
        {classDistribution && classDistribution.length > 0 && (
          <div
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginTop: '1.5rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#fff' }}>
                Top Classes
              </h3>
              {classDistribution.filter((item) => item.name !== 'Unknown' && item.name !== 'null')
                .length > 8 && (
                <span style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  Showing top 8 of{' '}
                  {
                    classDistribution.filter(
                      (item) => item.name !== 'Unknown' && item.name !== 'null'
                    ).length
                  }
                </span>
              )}
            </div>
            <div>
              <Row className="g-3">
                {classDistribution
                  .filter((item) => item.name !== 'Unknown' && item.name !== 'null')
                  .slice(0, 8)
                  .map((item, index) => (
                    <Col key={index} xs={6} md={4} lg={3}>
                      <div
                        style={{
                          background: 'transparent',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '12px',
                          padding: '1rem',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = item.color
                          e.currentTarget.style.transform = 'translateY(-2px)'
                          e.currentTarget.style.boxShadow = `0 4px 12px ${item.color}40`
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <div
                          style={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: '#fff',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {item.value.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: item.color, fontWeight: 500 }}>
                          {item.name}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.5)',
                            marginTop: '0.25rem',
                          }}
                        >
                          {totalAnnotations > 0
                            ? ((item.value / totalAnnotations) * 100).toFixed(1)
                            : '0'}
                          % of annotations
                        </div>
                      </div>
                    </Col>
                  ))}
              </Row>
            </div>
          </div>
        )}
      </ScrollableContent>
    </ViewContainer>
  )
}

export default DatasetOverviewTab
